import React, { FC, Fragment, useEffect, useState, useCallback } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { getSearchTemplate } from 'apollo/templates/search';
import Autocomplete from '@mui/material/Autocomplete';
import SearchCustomInput from 'components/navigation/app-bar/search/search-custom-input.component';
import ResultCard from 'components/navigation/app-bar/search/result-card.component';
import { SearchResult } from 'models/search-result';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useNavigate } from 'react-router-dom';
import { Box, Theme } from '@mui/system';
import { LinearProgress } from '@mui/material';
import { debounce } from 'lodash';

const Search: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'orders']);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [instructions, setInstructions] = useState<string>();
    const [hasPerformedSearch, setHasPerformedSearch] = useState(false);
    const currentUser = currentUserVar();
    const navigate = useNavigate();

    const [getSearchResults] = useLazyQuery(getSearchTemplate, {
        variables: {
            searchString: '',
        },
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err, 'err');
        },
        onCompleted: ({ search }) => {
            setHasPerformedSearch(true);
            setSearchResults(search || []);
            setLoading(false);
        },
    });

    const debouncedSearch = useCallback(
        debounce((searchText: string) => {
            if (searchText.length > 2) {
                setLoading(true);
                setOpen(true);
                getSearchResults({
                    variables: {
                        searchString: searchText,
                    },
                });
            }
        }, 300),
        [],
    );

    useEffect(() => {
        debouncedSearch(searchInputValue);
        return debouncedSearch.cancel;
    }, [searchInputValue, debouncedSearch]);

    useEffect(() => {
        if (loading) {
            setInstructions(undefined);
            return;
        }
        if (!hasPerformedSearch && searchInputValue.length > 0 && searchResults.length === 0) {
            setInstructions(translate('orders:TYPE_TO_SEARCH'));
            return;
        } else if (hasPerformedSearch && searchResults.length === 0) {
            setInstructions(translate('navigation:NO_SEARCH_RESULTS'));
            return;
        }
        setInstructions(undefined);
    }, [searchInputValue, searchResults, hasPerformedSearch, loading]);

    const handleSearchInput = (event: React.SyntheticEvent, searchText: string, reason: string) => {
        setSearchInputValue(searchText);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (
            event.key !== 'ArrowDown' &&
            event.key !== 'ArrowUp' &&
            event.key !== 'ArrowLeft' &&
            event.key !== 'ArrowRight'
        ) {
            setHasPerformedSearch(false);
        }
    };

    const handleSelectSearchResult = (event: React.SyntheticEvent, item: SearchResult | string | null) => {
        const searchResult = item as SearchResult;
        if (searchResult?._id && searchResults.length > 0) {
            setOpen(false);
            setSearchResults([]);
            setSearchInputValue('');
            setHasPerformedSearch(false);
            setLoading(false);
            setInstructions(undefined);
            navigate(`/order-details/${searchResult._id}/main-view`);
        }
    };

    return (
        <Fragment>
            <Box sx={{ marginTop: '2px' }}>
                <Autocomplete
                    id="search"
                    freeSolo
                    selectOnFocus
                    ListboxProps={{ style: { maxHeight: '90vh' } }}
                    autoHighlight
                    loading={instructions !== undefined}
                    loadingText={instructions}
                    getOptionLabel={(searchItem: SearchResult | string) => {
                        if (typeof searchItem === 'string') {
                            return searchItem || '';
                        }
                        return '';
                    }}
                    onChange={handleSelectSearchResult}
                    options={searchResults}
                    filterOptions={(_) => _}
                    open={open}
                    onBlur={() => setOpen(false)}
                    onFocus={() => setOpen(true)}
                    includeInputInList
                    inputValue={searchInputValue}
                    renderInput={(params) => (
                        <SearchCustomInput
                            type="search"
                            className="wl-search-input"
                            sx={(theme: Theme) => ({
                                searchInput: {
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: 200,
                                    },
                                },
                            })}
                            ref={params.InputProps.ref}
                            inputProps={{
                                ...params.inputProps,
                                onKeyDown: handleKeyDown,
                            }}
                            autoFocus
                            placeholder={translate('navigation:SEARCH_ORDER')}
                            startAdornment={<SearchIcon className="wl-text" />}
                            data-test="main-app-bar.search-input"
                        />
                    )}
                    onInputChange={handleSearchInput}
                    renderOption={(props, searchItem: SearchResult) => (
                        <li
                            {...props}
                            key={searchItem._id}
                            style={{ borderBottom: '1px solid', borderBottomColor: '#eee' }}
                        >
                            <ResultCard searchResult={searchItem} userType={currentUser?.type} />
                        </li>
                    )}
                />
                <Box sx={{ height: '2px' }}>{loading && <LinearProgress color="secondary" />}</Box>
            </Box>
        </Fragment>
    );
};

export default Search;
